import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { BoxTransactionsDetailsLayout } from "@modules/finances/components/boxTransactionDetailsLayout"
import { UserTransactionsDetailsLayout } from "@modules/finances/components/userTransactionsDetailsLayout"
import {
  PayoutDetailsDownloadModal,
  PayoutDetailsDownloadModalRef,
} from "@modules/financesPayout/components/payoutDetailsDownloadModal"
import {
  PayoutDetailsHelpModal,
  PayoutDetailsHelpModalRef,
} from "@modules/financesPayout/components/payoutDetailsHelpModal"
import { PayoutDetailsInfoSkeleton } from "@modules/financesPayout/components/payoutDetailsInfoSkeleton"
import { PayoutStatusWithIcon } from "@modules/financesPayout/components/payoutStatusWithIcon"
import { PayoutTransactionEntity } from "@modules/financesPayout/entities/payoutEntity"
import { getWordBalanceTransaction } from "@modules/financesPayout/entities/payoutUtils"
import { PayoutEnumApi } from "@modules/financesPayout/gateway/payoutApi"
import { usePayout } from "@modules/financesPayout/hooks/usePayout"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HuFlag } from "@modules/ui/components/huFlag"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { primaryFonts } from "@modules/ui/styles/font"
import dayjs from "dayjs"
import { Download } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTheme } from "styled-components"

type PayoutDetailsInfoProps = {
  payoutEnumApi: PayoutEnumApi
  payoutId: string
} & React.HTMLAttributes<HTMLDivElement>

export const PayoutDetailsInfo: React.FC<PayoutDetailsInfoProps> = ({ payoutEnumApi, payoutId, ...props }) => {
  const theme = useTheme()
  const language = useLanguage()
  const payoutDetailsHelpModal = useRef<PayoutDetailsHelpModalRef>(null)
  const payoutDetailsDownloadModal = useRef<PayoutDetailsDownloadModalRef>(null)
  const { data: payout, isLoading } = usePayout(payoutEnumApi, payoutId)
  const { sidebarNavigateTo, setCurrentStackState, getCurrentStackState } = useSidebar()
  const [scrollPosition, setScrollPosition] = useState(0)
  const [scrollPositionTransactionId, setScrollPositionTransactionId] = useState<string | null>(null)
  const containerScrollRef = useRef<HTMLDivElement>(null)

  const summaryHeaders = [
    { label: word("finances.payoutDetails.summaryHeaders.quantity"), col: "col-1" },
    { label: word("finances.payoutDetails.summaryHeaders.gross"), col: "col-3" },
    { label: word("finances.payoutDetails.summaryHeaders.fees"), col: "col-2" },
    { label: word("finances.payoutDetails.summaryHeaders.total"), col: "col-3" },
  ]
  const salesHeaders = [
    { label: word("finances.payoutDetails.salesHeaders.type"), col: "col-3 p-0 py-2" },
    { label: word("finances.payoutDetails.salesHeaders.gross"), col: "col-2 p-0 py-2" },
    { label: word("finances.payoutDetails.salesHeaders.net"), col: "col-2 p-0 py-2" },
    { label: word("finances.payoutDetails.salesHeaders.name"), col: "col-3 p-0 py-2" },
    { label: word("finances.payoutDetails.salesHeaders.date"), col: "col-2 p-0 py-2" },
  ]

  const bgSelectedPayout = PayoutEnumApi.OWNER === payoutEnumApi ? "bg-electricPurple-800" : "bg-leGreen-300"

  const handleClickTransaction = (transaction: PayoutTransactionEntity) => {
    if (!transaction.transactionId) return
    setCurrentStackState("scrollPositionPayoutId", payoutId)
    setCurrentStackState("scrollPositionTransactionId", transaction.id)
    setCurrentStackState("scrollPosition", scrollPosition.toString())

    sidebarNavigateTo({
      content:
        payoutEnumApi === PayoutEnumApi.OWNER ? (
          <BoxTransactionsDetailsLayout transactionId={transaction.transactionId} />
        ) : (
          <UserTransactionsDetailsLayout transactionId={transaction.transactionId} />
        ),
      replaceAllComponents: false,
    })
  }

  const handleScroll = () => {
    if (containerScrollRef.current) {
      setScrollPosition(containerScrollRef.current.scrollTop)
    }
  }

  useEffect(() => {
    if (!isLoading) {
      const scrollTo = getCurrentStackState("scrollPosition")
      setScrollPositionTransactionId(getCurrentStackState("scrollPositionTransactionId"))
      containerScrollRef.current?.scrollTo(0, scrollTo ? parseInt(scrollTo, 10) : 0)
    }
  }, [isLoading, containerScrollRef.current])

  if (!payout || isLoading) {
    return <PayoutDetailsInfoSkeleton />
  }

  const payoutTransactionSummary = {
    payment: { label: word("finances.payoutDetails.transactionSummary.payment"), transaction: payout.paymentSummary },
    refund: { label: word("finances.payoutDetails.transactionSummary.refund"), transaction: payout.refundSummary },
    adjustment: {
      label: word("finances.payoutDetails.transactionSummary.adjustement"),
      transaction: payout.adjustmentSummary,
    },
  }

  return (
    <>
      <div className="relative" style={{ marginTop: "-85px", marginLeft: "-20px", marginRight: "-20px" }} {...props}>
        {/* HEADER */}
        <div className="relative flex justify-content-between px-5 pt-8 pb-5 w-full  bg-neutralColor-50 border-round-bottom-3xl">
          {/* Column paid */}
          <div className="flex flex-column justify-content-between">
            <div className="flex flex-column gap-1">
              <HUText fontStyle="HS" color={theme.blackAtheleWhiteOwner}>
                {formatCurrency(payout.amount, payout.currency, language)}
              </HUText>
              <PayoutStatusWithIcon
                payoutStatus={payout.status}
                size={24}
                color={theme.sucess}
                fontStyle="BM"
                colorText={theme.blackAtheleWhiteOwner}
              />
            </div>
            {payout.bankName && payout.last4Digits && (
              <div className="flex flex-column gap-1 mt-4">
                <HUText fontStyle="LS" color={theme.neutralColor600}>
                  {word("finances.payoutDetails.accountInformation")}
                </HUText>
                <div className="flex flex-wrap gap-2 justify-content-between">
                  <HUText color={theme.blackAtheleWhiteOwner} fontStyle="BM">
                    {`${payout.bankName} ${payout.getFormatedDigits()}`}
                  </HUText>
                  <div className="flex gap-1">
                    <HUText color={theme.blackAtheleWhiteOwner} fontStyle="BM">
                      {payout.bankCountry}
                    </HUText>
                    <HuFlag
                      className="flex border-neutral-700 border-1 border-round-lg"
                      countryCode={payout.bankCountry || ""}
                      svg={true}
                      style={{ width: "1.4rem", height: "1.2rem", objectFit: "cover" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Column timeline */}
          <div className="flex flex-column">
            <HUText fontStyle="BS" color={theme.neutralColor600} className="mb-3">
              {word("finances.payoutDetails.timeline")}
            </HUText>

            <div className="flex flex-column gap-2">
              <div className="flex gap-2">
                <div
                  className="flex"
                  style={{ width: "16px", height: "16px", borderRadius: "5.6px", background: theme.primaryColor400 }}
                ></div>
                <div className="flex flex-column gap-1">
                  <HUText fontStyle="LM" color={theme.neutralColor600}>
                    {word("finances.payoutDetails.transferMade")}
                  </HUText>
                  <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
                    {dayjs(payout.createdAt).format("ll")}
                  </HUText>
                </div>
                <HUButton
                  type="Default"
                  size="M"
                  colorType="Underline"
                  text={word("global.help")}
                  onClick={() => {
                    payoutDetailsHelpModal.current?.open()
                  }}
                  className="flex align-items-start p-0 ml-2"
                />
              </div>

              {payout.intervalStartDate && payout.intervalEndDate && (
                <div className="flex gap-2">
                  <div
                    className="flex"
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "5.6px",
                      background: theme.neutralColor600,
                    }}
                  ></div>
                  <div className="flex flex-column gap-1">
                    <HUText fontStyle="LS" color={theme.neutralColor600}>
                      {word("finances.payoutDetails.transferInTransit")}
                    </HUText>
                    <HUText fontStyle="LM" color={theme.blackAtheleWhiteOwner}>
                      {dayjs(payout.intervalStartDate).format("ll")} - {dayjs(payout.intervalEndDate).format("ll")}
                    </HUText>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <div
                  className="flex"
                  style={{ width: "16px", height: "16px", borderRadius: "5.6px", background: theme.neutralColor600 }}
                ></div>
                <div className="flex flex-column gap-1">
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {word("finances.payoutDetails.automatedTransfer")}
                  </HUText>
                  <HUText fontStyle="LM" color={theme.blackAtheleWhiteOwner}>
                    {dayjs(payout.expectedReceptionDate).format("lll")}
                  </HUText>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-column w-full">
        {/* SUMMARY */}
        <div className="flex w-full flex-column mt-6">
          <HUText fontStyle="LM" color={theme.neutralColor600}>
            {word("finances.payoutDetails.headerTab.summary").toUpperCase()}
          </HUText>
          <div className="grid">
            <div className="col"></div>
            {summaryHeaders.map((header) => (
              <div className={header.col} key={`${header.label}_SUMMARY`}>
                <HUText fontStyle="LM" color={theme.neutralColor600}>
                  {header.label}
                </HUText>
              </div>
            ))}
          </div>
          <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full mb-2" />
          {Object.values(payoutTransactionSummary).map((summary) => (
            <div className="flex w-full flex-column pt-2" key={summary.label}>
              <div className="grid align-items-end">
                <div className="col">
                  <HUText
                    fontStyle="LM"
                    color={theme.blackAtheleWhiteOwner}
                    style={{ lineHeight: primaryFonts.BM.lineHeight }}
                  >
                    {summary.label}
                  </HUText>
                </div>
                <div className="col-1">
                  <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-all" }}>
                    {summary.transaction.number}
                  </HUText>
                </div>
                <div className="col-3">
                  <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-all" }}>
                    {formatCurrency(summary.transaction.totalAmount ?? 0, payout.currency, language)}
                  </HUText>
                </div>
                <div className="col-2">
                  <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-all" }}>
                    {formatCurrency(summary.transaction.feeAmount ?? 0, payout.currency, language)}
                  </HUText>
                </div>
                <div className="col-3">
                  <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-all" }}>
                    {formatCurrency(summary.transaction.getTotalLessFee(), payout.currency, language)}
                  </HUText>
                </div>
              </div>
              <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full" />
            </div>
          ))}
        </div>
        {/* TRANSFER */}
        <div className="flex justify-content-end align-items-baseline w-full gap-4 mt-3">
          <HUText fontStyle="LM" color={theme.blackAtheleWhiteOwner}>
            {word("finances.payoutDetails.transfer")}
          </HUText>
          <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner}>
            {formatCurrency(payout.amount, payout.currency, language)}
          </HUText>
        </div>
        {/* SALES */}
        <div className="flex w-full flex-column mt-6">
          <div className="flex w-full align-items-center justify-content-between mb-3">
            <HUText fontStyle="LM" color={theme.neutralColor600}>
              {word("finances.payoutDetails.headerTab.sales").toUpperCase()}
            </HUText>
            <HUButton
              text={word("global.download")}
              onClick={() => {
                payoutDetailsDownloadModal.current?.open()
              }}
              icon={{
                iconView: <Download size={15} color={theme.blackAtheleWhiteOwner} />,
              }}
              type="Right_Icon"
              size="M"
              colorType="Primary"
              style={{ padding: "8px" }}
            />
          </div>

          <div className="grid w-full px-2">
            {salesHeaders.map((header) => (
              <div className={header.col} key={`${header.label}_SALES`}>
                <HUText fontStyle="LM" color={theme.neutralColor600} style={{ wordBreak: "break-all" }}>
                  {header.label}
                </HUText>
              </div>
            ))}
          </div>

          <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full" />
          <div
            style={{ height: "320px", overflow: "scroll" }}
            className="w-full"
            onScroll={handleScroll}
            ref={containerScrollRef}
          >
            {payout.transactions.map((transaction) => (
              <div
                className={`flex flex-column w-full px-2 ${
                  scrollPositionTransactionId === transaction.id ? bgSelectedPayout : "hover:surface-hover"
                }`}
                key={transaction.id}
              >
                <div
                  className={`grid align-items-start pt-3 w-full ${transaction.transactionId ? "cursor-pointer" : ""}`}
                  onClick={() => handleClickTransaction(transaction)}
                >
                  <div className="col-3">
                    <HUText fontStyle="BS" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-all" }}>
                      {getWordBalanceTransaction(transaction.type)}
                    </HUText>
                  </div>
                  <div className="col-2">
                    <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-word" }}>
                      {formatCurrency(transaction.amount, transaction.currency, language)}
                    </HUText>
                  </div>
                  <div className="col-2">
                    <div className="flex flex-column gap-1">
                      <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-word" }}>
                        {formatCurrency(transaction.amount - transaction.fee, transaction.currency, language)}
                      </HUText>
                      <HUText fontStyle="LS" color={theme.neutralColor600} style={{ wordBreak: "break-word" }}>
                        {`-${formatCurrency(transaction.fee, transaction.currency, language)}`}
                      </HUText>
                    </div>
                  </div>
                  <div className="col-3">
                    <HUText fontStyle="BS" color={theme.blackAtheleWhiteOwner} style={{ wordBreak: "break-word" }}>
                      {transaction.name ?? "-"}
                    </HUText>
                  </div>
                  <div className="col-2">
                    <HUText
                      fontStyle="BS"
                      color={theme.blackAtheleWhiteOwner}
                      style={{ wordBreak: "break-word" }}
                      className="capitalize"
                    >
                      {dayjs(transaction.createdAt).format("MMM DD")}
                    </HUText>
                  </div>
                </div>
                <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full" />
              </div>
            ))}
          </div>
        </div>
        {/* FOOTER */}
        <div className="flex flex-column w-full gap-2 mt-6 mb-4">
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.internalNote")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner} className="capitalize">
              {payout.note?.toLowerCase() ?? "-"}
            </HUText>
          </div>
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.transferMethod")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner} className="capitalize">
              {payout.transferMethod.toLowerCase()}
            </HUText>
          </div>
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.transferTrackingId")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {payout.transferTrackingId ?? "-"}
            </HUText>
          </div>
        </div>
      </div>
      <PayoutDetailsHelpModal payoutDetails={payout} ref={payoutDetailsHelpModal} />
      <PayoutDetailsDownloadModal
        payoutDetails={payout}
        payoutEnumApi={payoutEnumApi}
        ref={payoutDetailsDownloadModal}
      />
    </>
  )
}
